<template>
  <div class="space-y-px">
    <div v-if="hasAssignedAffiliate">
      <div class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium" :class="statusClass">
        <span>{{ affiliateCompanyName }}</span>
      </div>
    </div>
    <div v-else class="relative">
      <input
        v-model="searchTerm"
        @focus="showDropdown = true"
        @blur="handleBlur"
        @input="filterAffiliates"
        @keydown.enter.prevent="selectFirstMatch"
        @keydown.escape="closeDropdown"
        @keydown.arrow-down.prevent="navigateDown"
        @keydown.arrow-up.prevent="navigateUp"
        type="text"
        placeholder="Search partner..."
        class="w-full text-xs rounded border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
      />

      <div
        v-if="showDropdown && filteredAffiliates.length > 0"
        class="absolute z-50 w-96 mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-64 overflow-y-auto"
      >
        <button
          v-for="(affiliate, index) in filteredAffiliates"
          :key="affiliate.id"
          @mousedown.prevent="selectAffiliate(affiliate)"
          :class="[
            'w-full text-left px-4 py-3 text-sm hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0',
            index === highlightedIndex ? 'bg-blue-100' : ''
          ]"
        >
          <div class="font-medium text-gray-900">{{ affiliate.name }}</div>
          <div v-if="affiliate.company_id" class="text-xs text-gray-500">ID: {{ affiliate.company_id }}</div>
        </button>
      </div>

      <div
        v-if="showDropdown && filteredAffiliates.length === 0 && searchTerm"
        class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg px-3 py-2 text-xs text-gray-500"
      >
        No partners found
      </div>
    </div>
    <div class="text-xs text-gray-500">{{ formattedAmount }}</div>
    <div v-if="mapping.status === 'paid'" class="text-center text-xxs uppercase tracking-wide text-green-700 font-bold"><i class="fa-regular fa-check-circle" /> Paid</div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue'
import axios from 'axios'

const props = defineProps({
  mapping: Object,
  affiliateCompanies: Array,
  routes: Object,
  commissionType: String
})

const emit = defineEmits(['mapped'])

const searchTerm = ref('')
const showDropdown = ref(false)
const highlightedIndex = ref(-1)
const filteredAffiliates = ref([...props.affiliateCompanies])

const affiliateCompanyName = computed(() => {
  if (props.mapping.affiliate_company?.company?.name) {
    return props.mapping.affiliate_company.company.name
  }
  return 'Deleted'
})

const hasAssignedAffiliate = computed(() => {
  return ['mapped', 'paid'].includes(props.mapping.status)
})

const statusClass = computed(() => {
  if (props.mapping.status === 'paid') {
    return 'bg-green-100 text-green-800'
  }

  return 'bg-blue-100 text-blue-800'
})

const formattedAmount = computed(() => {
  const amount = props.mapping.commission_amount_cents / 100
  return `${amount.toFixed(2)} EUR`
})

const filterAffiliates = () => {
  if (!searchTerm.value) {
    filteredAffiliates.value = [...props.affiliateCompanies]
  } else {
    filteredAffiliates.value = props.affiliateCompanies.filter(affiliate =>
      affiliate.name.toLowerCase().includes(searchTerm.value.toLowerCase())
    )
  }
  highlightedIndex.value = -1
}

const selectAffiliate = async (affiliate) => {
  searchTerm.value = affiliate.name
  showDropdown.value = false

  try {
    await axios.post(props.routes.map, {
      mapping_id: props.mapping.id,
      affiliate_company_id: affiliate.id
    })

    emit('mapped')
  } catch (error) {
    console.error('Error mapping commission:', error)
    alert('Error mapping commission')
    searchTerm.value = '' // Reset on error
  }
}

const selectFirstMatch = () => {
  if (filteredAffiliates.value.length > 0) {
    const affiliate = highlightedIndex.value >= 0
      ? filteredAffiliates.value[highlightedIndex.value]
      : filteredAffiliates.value[0]
    selectAffiliate(affiliate)
  }
}

const navigateDown = () => {
  if (highlightedIndex.value < filteredAffiliates.value.length - 1) {
    highlightedIndex.value++
  }
}

const navigateUp = () => {
  if (highlightedIndex.value > 0) {
    highlightedIndex.value--
  }
}

const closeDropdown = () => {
  showDropdown.value = false
  highlightedIndex.value = -1
}

const handleBlur = () => {
  // Use nextTick to allow click events to fire before hiding dropdown
  nextTick(() => {
    showDropdown.value = false
    highlightedIndex.value = -1
  })
}
</script>
